const INSTALLATION_ID_KEY = "runtimeInstallationId";
const INSTALLATION_SECRET_KEY = "runtimeInstallationSecret";
const RUNTIME_USER_KEY = "runtimeUserData";
const SESSION_EXPIRY_SKEW_MS = 60_000;

const encodeBytes = (bytes) => {
  let binary = "";
  bytes.forEach((value) => {
    binary += String.fromCharCode(value);
  });
  return btoa(binary)
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/g, "");
};

const createInstallationCredentials = () => {
  const installationId =
    typeof window.crypto?.randomUUID === "function"
      ? window.crypto.randomUUID()
      : `web-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const secretBytes = new Uint8Array(32);
  window.crypto.getRandomValues(secretBytes);
  return { installationId, installationSecret: encodeBytes(secretBytes) };
};

const getInstallationCredentials = () => {
  const existingId = window.localStorage.getItem(INSTALLATION_ID_KEY);
  const existingSecret = window.localStorage.getItem(INSTALLATION_SECRET_KEY);
  if (existingId && existingSecret) {
    return { installationId: existingId, installationSecret: existingSecret };
  }

  const credentials = createInstallationCredentials();
  window.localStorage.setItem(INSTALLATION_ID_KEY, credentials.installationId);
  window.localStorage.setItem(
    INSTALLATION_SECRET_KEY,
    credentials.installationSecret,
  );
  return credentials;
};

export const getWebRuntimeUserData = () => {
  if (typeof window === "undefined") return null;
  const value = window.sessionStorage.getItem(RUNTIME_USER_KEY);
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

export const getWebRuntimeAccessToken = () =>
  getWebRuntimeUserData()?.jwt || null;

const sessionIsCurrent = (userData) => {
  const expiry = new Date(userData?.expiresAt || "").getTime();
  return Boolean(
    userData?.id &&
      userData?.jwt &&
      Number.isFinite(expiry) &&
      expiry > Date.now() + SESSION_EXPIRY_SKEW_MS,
  );
};

export const getOrCreateWebRuntimeSession = async ({ force = false } = {}) => {
  if (typeof window === "undefined") {
    throw new Error("Web runtime sessions require a browser.");
  }
  const cached = getWebRuntimeUserData();
  if (!force && sessionIsCurrent(cached)) return cached;

  const baseUrl = process.env.REACT_APP_API_ACCOUNTS_URL;
  if (!baseUrl) throw new Error("Accounts API is not configured.");
  const response = await fetch(`${baseUrl.replace(/\/$/, "")}/login-mobile`, {
    body: JSON.stringify({
      ...getInstallationCredentials(),
      platform: "web",
      storeEntitlements: [],
      storeEntitlementsChecked: true,
    }),
    headers: { "Content-Type": "application/json" },
    method: "POST",
  });
  const payload = await response.json();
  if (!response.ok) {
    throw new Error(payload?.message || "Unable to start a web session.");
  }
  if (!payload?.id || !payload?.jwt || !payload?.expiresAt) {
    throw new Error("Accounts returned an incomplete web session.");
  }
  window.sessionStorage.setItem(RUNTIME_USER_KEY, JSON.stringify(payload));
  return payload;
};
